import PropTypes from 'prop-types';
import { lazy, Suspense } from 'react';
import Countdown, { zeroPad } from 'react-countdown';
import { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { DefaultLink } from '@hivebrite/design-system/Link';
import { Loader } from '@hivebrite/design-system/Loader';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';

const EventZoomClient = lazy(
  () => import('hb-react/shared/apps/Zoom/EventZoomClient'),
);

const shouldNotDisplayLink = (event, currentUser) => {
  if (!event.webinar_url && !event.is_internal_zoom_meeting) {
    return true;
  }
  if (!currentUser) {
    return true;
  }
  if (dayjs(event.end_date).isBefore(dayjs())) {
    return true;
  }
  if (!event.has_a_ticket && event.participation_type === 'tickets') {
    return true;
  }

  return (
    event.response_type !== 'attend' && event.participation_type === 'rsvp'
  );
};

const renderLink = (event) => (
  <Box flexDirection="column" width="100%">
    {event.is_internal_zoom_meeting ? (
      <Suspense fallback={<Loader size={14} />}>
        <EventZoomClient eventId={event.id} />
        <Box width="100%">
          {event.webinar_url && (
            <Box>
              {s__(
                'Zoom|Having trouble loading the zoom window, please click ',
              )}
              <DefaultLink href={event.webinar_url} target="_blank">
                &nbsp;{s__('Zoom|here')}&nbsp;
              </DefaultLink>
              {s__('Zoom| to open in a new window.')}
            </Box>
          )}
        </Box>
      </Suspense>
    ) : (
      <DefaultLink
        className="f-size--1-5 weight--bold soft-half"
        href={event.webinar_url}
        target="_blank"
        rel="noreferrer"
      >
        {__('Click here to join event')}
      </DefaultLink>
    )}
  </Box>
);

const renderTime = (value, text, first) => (
  <Box
    bg="smoke"
    width={90}
    alignItems="center"
    flexDirection="column"
    ml={!first && 5}
    py={4}
    css={css`
      border-radius: 3px;
    `}
  >
    <Box font="h2-semibold" mb={2}>
      {value}
    </Box>
    <Box>{text}</Box>
  </Box>
);

const renderer = ({ days, hours, minutes, seconds, completed }, event) => {
  if (completed) {
    return renderLink(event);
  }

  return (
    <div className="flex flex-wrap virtual-event-link-countdown">
      {renderTime(zeroPad(days), __('Days'), true)}
      {renderTime(zeroPad(hours), __('Hours'))}
      {renderTime(zeroPad(minutes), __('Minutes'))}
      {renderTime(zeroPad(seconds), __('Seconds'))}
    </div>
  );
};

const VirtualEventLink = ({ event, className }) => {
  const { currentUser } = useDefaultAppContexts();

  if (shouldNotDisplayLink(event, currentUser)) {
    return null;
  }

  return (
    <Box className={className}>
      {dayjs(event.start_date).isAfter(dayjs()) ? (
        <Countdown
          date={event.start_date}
          renderer={(props) => renderer(props, event)}
        />
      ) : (
        renderLink(event)
      )}
    </Box>
  );
};

VirtualEventLink.propTypes = {
  event: PropTypes.object.isRequired,
};

export default VirtualEventLink;
